// Answering the pinned approval (D85): an optional note, then one button per option. The note
// is encrypted on this browser into the task's conversation (the newest segment it holds the
// key of) before the signed answer goes, so the daemon finds it for the next step; it needs a
// managed account key, the only key the daemon can open it with.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button, Textarea } from "rizzui";

import { fetchAccountKey } from "../../account/api";
import { useConversation } from "../../conversation/useConversation";
import type { DeviceState } from "../../device/context";
import { useT } from "../../i18n";
import { toast } from "../../layout/toast";
import type { Proto } from "../../proto/index";
import { type PendingApproval, MAX_NOTE_CHARS, notePlaintext } from "./approval";
import { answerApproval } from "./answer";

type Linked = DeviceState & { kind: "linked" };

interface Props {
  proto: Proto;
  device: Linked;
  taskId: string;
  conversationId: string | null;
  pending: PendingApproval;
}

export function ApprovalAnswer(props: Props) {
  if (props.conversationId === null) return <ApprovalAnswerForm {...props} send={undefined} />;
  return <WithConversation {...props} conversationId={props.conversationId} />;
}

function WithConversation(props: Props & { conversationId: string }) {
  const { send } = useConversation(props.device, props.conversationId);
  return <ApprovalAnswerForm {...props} send={send} />;
}

export type Send = ReturnType<typeof useConversation>["send"];

/** The note and the option buttons; `send` encrypts into the task's conversation. */
export function ApprovalAnswerForm({ proto, device, taskId, pending, send }: Props & { send: Send | undefined }) {
  const t = useT().tasks.approvalCard;
  const queryClient = useQueryClient();
  const key = useQuery({ queryKey: ["account-key"], queryFn: fetchAccountKey });
  const managed = key.data?.mode === "managed";
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const canNote = managed && send !== undefined;

  async function answer(option: string) {
    setBusy(option);
    setError(undefined);
    try {
      const text = note.trim();
      if (text && canNote) {
        await send({ eventType: "instruction", text: notePlaintext(taskId, pending.node, text), anySegment: true });
      }
      await answerApproval(proto, device.device, taskId, pending.node, option);
      toast(text && canNote ? t.answeredWithNote(option) : t.answered(option));
      setNote("");
      await queryClient.invalidateQueries({ queryKey: ["tasks", taskId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <div className="space-y-3">
      <Textarea
        label={t.noteLabel}
        placeholder={t.notePlaceholder}
        rows={2}
        maxLength={MAX_NOTE_CHARS}
        value={note}
        disabled={!canNote || busy !== undefined}
        onChange={(e) => setNote(e.target.value)}
      />
      <p className="m-0 text-[12px] text-gray-500">{canNote ? t.noteHint : managed ? t.noteNoKey : t.noteSelfManaged}</p>
      <div className="flex flex-wrap gap-2">
        {pending.options.map((option, i) => (
          <Button
            key={option}
            size="sm"
            variant={i === 0 ? "solid" : "outline"}
            isLoading={busy === option}
            disabled={busy !== undefined && busy !== option}
            onClick={() => void answer(option)}
          >
            {note.trim() && canNote ? t.answerWithNote(option) : option}
          </Button>
        ))}
      </div>
      {error ? (
        <p role="alert" className="m-0 text-[12.5px] text-red-dark">
          {error}
        </p>
      ) : null}
    </div>
  );
}
